import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import Snackbar from '@/components/Snackbar';
import { PageTitle } from '../../blocks';
import { useRoleScope } from '../hooks';
import DeliveryProfileModal from '../DeliveryProfileModal';
import PageNotice from '../PageNotice';
import ProfilesTable from './ProfilesTable';
import { useProfilesGrid } from './hooks';
import { profilePath } from './utils';

// Auto Assign's entry page: every delivery profile in the user's scope. A profile's name opens its
// page; Create and Edit use the profile modal here.
const DeliveryProfilesGrid = () => {
    const navigate = useNavigate();
    const scope = useRoleScope();
    const [notification, setNotification] = useState({});
    // null when closed; `{ profile }` when open, with a null profile for Create.
    const [profileModal, setProfileModal] = useState(null);

    const { rows, shipperOptions, profilesById, upsertProfile, loading } = useProfilesGrid(scope, {
        onError: (message) => setNotification({ severity: 'error', message }),
    });

    const onEdit = useCallback((id) => setProfileModal({ profile: profilesById[id] || null }), [profilesById]);

    // A new profile opens on its own page; an edit stays on the grid.
    const upsertDeliveryProfile = useCallback(
        (values, options) =>
            upsertProfile(values, options).then((res) => {
                const saved = res?.data?.result;
                if (saved?.delivery_profile_id && !values.delivery_profile_id) {
                    navigate(profilePath(saved.delivery_profile_id));
                }
                return res;
            }),
        [upsertProfile, navigate]
    );

    if (scope.kind === 'corp' && !scope.org_id) {
        return (
            <PageNotice title="Delivery Profiles">
                <p>Your account is not associated with an organization — contact support.</p>
            </PageNotice>
        );
    }

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                background: ${colors.white.primary};
                flex-wrap: nowrap;
            `}
        >
            <Snackbar open={!!notification.message} {...notification} handleClose={() => setNotification({})} />
            <DeliveryProfileModal
                open={!!profileModal}
                onClose={() => setProfileModal(null)}
                deliveryProfile={profileModal?.profile || null}
                callbacks={{ upsertDeliveryProfile }}
            />
            <Grid
                container
                css={css`
                    padding: 23px 45px;
                    border-bottom: 1px solid ${colors.greys[0]};
                    align-items: center;
                    flex: 0;
                `}
            >
                <PageTitle>Delivery Profiles</PageTitle>
            </Grid>
            <Grid
                item
                css={css`
                    flex: 1;
                    min-height: 0;
                `}
            >
                <ProfilesTable
                    rows={rows}
                    shipperOptions={shipperOptions}
                    loading={loading}
                    onCreate={() => setProfileModal({ profile: null })}
                    onEdit={onEdit}
                />
            </Grid>
        </Grid>
    );
};

export default DeliveryProfilesGrid;
